/**
 * Types and functions for PolyLine3 - a segmented 3d path that may be closed. A PolyLine3 is just a
 * typed array and may be closed by adding an end vertex coincident with the start index.
 * @module
 */
import type { AABB3 } from "./aabb3Types";
import { aabb3New } from "./aabb3Types";
import { ANGLE_EPSILON, DIST_EPSILON, DIST_EPSILON_SQUARED } from "./core";
import { line3Eval, line3Slope, line3Tangent } from "./line3";
import { geomGetAllocator } from "./mem";
import type { Plane } from "./planeTypes";
import type { PolyLine2 } from "./polyLine2Types";
import { polyLine2New } from "./polyLine2Types";
import { ray3Eval } from "./ray3";
import { vec2Dist } from "./vec2";
import type { Vec3 } from "./vec3";
import {
  vec3Cross,
  vec3CrossNormalize,
  vec3Dist,
  vec3DistSquared,
  vec3Result,
  vec3Scale,
  vec3SequenceIsTurning,
} from "./vec3";

const { sin, cos } = Math;

export type PolyLine3 = Float64Array<ArrayBuffer>;
export const PolyLine3 = Float64Array;

export function polyLine3New(size: number | ArrayLike<number>): PolyLine3 {
  const alloc = geomGetAllocator();

  if (typeof size === "number") {
    return alloc.allocFloat64(size) as PolyLine3;
  }

  const v = alloc.allocFloat64(size.length);
  v.set(size, 0);
  return v as PolyLine3;
}

export function polyLine3ByArray(elements: number[]): PolyLine3 {
  return polyLine3New(elements);
}

export function polyLine3ByTwoPoints(
  x0: number,
  y0: number,
  z0: number,
  x1: number,
  y1: number,
  z1: number,
): PolyLine3 {
  const pl = polyLine3New(6);

  pl[0] = x0;
  pl[1] = y0;
  pl[2] = z0;
  pl[3] = x1;
  pl[4] = y1;
  pl[5] = z1;

  return pl;
}

export function polyLine3ByThreePoints(
  x0: number,
  y0: number,
  z0: number,
  x1: number,
  y1: number,
  z1: number,
  x2: number,
  y2: number,
  z2: number,
): PolyLine3 {
  const pl = polyLine3New(9);

  pl[0] = x0;
  pl[1] = y0;
  pl[2] = z0;
  pl[3] = x1;
  pl[4] = y1;
  pl[5] = z1;
  pl[6] = x2;
  pl[7] = y2;
  pl[8] = z2;

  return pl;
}

export function polyLine3Move(pl: PolyLine3, x: number, y: number, z: number) {
  for (let i = 0, l = pl.length; i < l; i += 3) {
    pl[i] += x;
    pl[i + 1] += y;
    pl[i + 2] += z;
  }

  return pl;
}

export function polyLine3Compare(a: PolyLine3, b: PolyLine3, tol: number = DIST_EPSILON): boolean {
  if (a.length !== b.length) {
    return false;
  }

  const tolSq = tol * tol;

  for (let i = 0, l = a.length; i < l; i += 3) {
    if (vec3DistSquared(a[i], a[i + 1], a[i + 2], b[i], b[i + 1], b[i + 2]) > tolSq) {
      return false;
    }
  }

  return true;
}

export function polyLine3Close(pl: PolyLine3): PolyLine3 {
  if (polyLine3IsClosed(pl)) {
    return pl;
  }

  const l = pl.length;
  const pln = polyLine3New(l + 3);

  pln.set(pl, 0);

  pln[l] = pl[0];
  pln[l + 1] = pl[1];
  pln[l + 2] = pl[2];

  return pln;
}

export function polyLine3Reverse(pl: PolyLine3) {
  const ll = pl.length;
  for (let i = 0, l = ll / 2; i < l; i += 3) {
    const x = pl[i];
    const y = pl[i + 1];
    const z = pl[i + 2];

    const ax = pl[ll - i - 3];
    const ay = pl[ll - i - 2];
    const az = pl[ll - i - 1];

    pl[i] = ax;
    pl[i + 1] = ay;
    pl[i + 2] = az;

    pl[ll - i - 3] = x;
    pl[ll - i - 2] = y;
    pl[ll - i - 1] = z;
  }
  return pl;
}

export function polyLine3AtZ(pl: PolyLine2, z: number = 0): PolyLine3 {
  const res = polyLine3New((pl.length / 2) * 3);

  for (let i = 0, l = pl.length / 2; i < l; i++) {
    const i2 = 2 * i;
    const i3 = 3 * i;

    res[i3] = pl[i2];
    res[i3 + 1] = pl[i2 + 1];
    res[i3 + 2] = z;
  }

  return res;
}

export function polyLine3RemoveDuplicateNeighbors(pl: PolyLine3) {
  const plen = pl.length;
  let cursor = 3;
  for (let i = 0, l = plen - 3; i < l; i += 3) {
    const x0 = pl[i];
    const y0 = pl[i + 1];
    const z0 = pl[i + 2];

    const x1 = pl[i + 3];
    const y1 = pl[i + 4];
    const z1 = pl[i + 5];

    pl[cursor] = x1;
    pl[cursor + 1] = y1;
    pl[cursor + 2] = z1;

    if (vec3DistSquared(x0, y0, z0, x1, y1, z1) > DIST_EPSILON_SQUARED) {
      cursor += 3;
    }
  }

  if (cursor === plen) {
    return pl;
  }

  const a = polyLine3New(cursor);
  a.set(pl.slice(0, cursor));
  return a;
}

export function polyLine3OnPlane(
  pl: ArrayLike<number>,
  { originX, originY, originZ, uX, uY, uZ, vX, vY, vZ }: Plane,
): PolyLine3 {
  const res = polyLine3New((pl.length / 2) * 3);

  for (let i = 0, l = pl.length / 2; i < l; i++) {
    const i2 = 2 * i;
    const i3 = 3 * i;

    const u = pl[i2];
    const v = pl[i2 + 1];

    res[i3] = originX + uX * u + vX * v;
    res[i3 + 1] = originY + uY * u + vY * v;
    res[i3 + 2] = originZ + uZ * u + vZ * v;
  }

  return res;
}

export function polyLine3IsClosed(pl: PolyLine3): boolean {
  return (
    vec3DistSquared(pl[0], pl[1], pl[2], pl[pl.length - 3], pl[pl.length - 2], pl[pl.length - 1]) < DIST_EPSILON_SQUARED
  );
}

export function polyLine3CopyReversed(pl: PolyLine3): PolyLine3 {
  return polyLine3Reverse(polyLine3Copy(pl));
}

export function polyLine3EdgeSlopeInZ(pl: PolyLine3, edgeIndex: number): number {
  const i = edgeIndex * 3;

  const x0 = pl[i];
  const y0 = pl[i + 1];
  const z0 = pl[i + 2];

  const x1 = pl[i + 3];
  const y1 = pl[i + 4];
  const z1 = pl[i + 5];

  const run = vec2Dist(x0, y0, x1, y1);
  return (z1 - z0) / run;
}

export function polyLine3Scale(pl: PolyLine3, s: number) {
  for (let i = 0, l = pl.length; i < l; i++) {
    pl[i] *= s;
  }
  return pl;
}

export function polyLine3NonUniformScale(pl: PolyLine3, sx: number, sy: number, sz: number) {
  for (let i = 0, l = pl.length; i < l; i += 3) {
    pl[i] *= sx;
    pl[i + 1] *= sy;
    pl[i + 2] *= sz;
  }
  return pl;
}

export function polyLine3NonUniformScaleAndKeepCenter(pl: PolyLine3, sx: number, sy: number, sz: number) {
  const aabb = polyLine3AABB3(pl);
  const centerX = 0.5 * (aabb.minX + aabb.maxX);
  const centerY = 0.5 * (aabb.minY + aabb.maxY);
  const centerZ = 0.5 * (aabb.minZ + aabb.maxZ);
  return polyLine3Move(
    polyLine3NonUniformScale(polyLine3Move(pl, -centerX, -centerY, -centerZ), sx, sy, sz),
    centerX,
    centerY,
    centerZ,
  );
}

export function polyLine3Concat(a: ArrayLike<number>, b: ArrayLike<number>, close: boolean = false): PolyLine2 {
  const c = polyLine3New(a.length + b.length + (close ? 3 : 0));
  c.set(a, 0);
  c.set(b, a.length);
  if (close) {
    c[c.length - 3] = c[0];
    c[c.length - 2] = c[1];
    c[c.length - 1] = c[2];
  }
  return c;
}

export function polyLine3ConcatAll(pls: ArrayLike<number>[], close: boolean = false): PolyLine3 {
  let totalLen = 0;

  const polylineCount = pls.length;
  for (let i = 0, l = polylineCount; i < l; i++) {
    totalLen += pls[i].length;
  }

  if (close && totalLen >= 3) {
    totalLen += 3;
  }

  const c = polyLine3New(totalLen);

  let offset = 0;
  for (let i = 0, l = polylineCount; i < l; i++) {
    const pl = pls[i];

    c.set(pl, offset);
    offset += pl.length;
  }

  if (close && totalLen >= 3) {
    c[c.length - 3] = c[0];
    c[c.length - 2] = c[1];
    c[c.length - 1] = c[2];
  }

  return c;
}

export function polyLine3DistBetweenVerts(pl: PolyLine3, a: number, b: number): number {
  const aoffset = a * 3;
  const ax = pl[aoffset];
  const ay = pl[aoffset + 1];
  const az = pl[aoffset + 2];

  const boffset = b * 3;
  const bx = pl[boffset];
  const by = pl[boffset + 1];
  const bz = pl[boffset + 2];

  return vec3Dist(ax, ay, az, bx, by, bz);
}

export function polyLine3ExtendBothEnds(pl: PolyLine3, distStart: number, distEnd: number = distStart) {
  return polyLine3ExtendEnd(polyLine3ExtendStart(pl, distStart), distEnd);
}

export function polyLine3ExtendEnd(pl: PolyLine3, dist: number, limitToEdgeLen: boolean = false) {
  const lastEdge = polyLine3EdgeCount(pl) - 1;
  const edgeLen = polyLine3EdgeLength(pl, lastEdge);
  const finalDist = limitToEdgeLen ? Math.max(Math.min(dist, edgeLen), -edgeLen) : dist;

  const [tx, ty, tz] = polyLine3EdgeTangent(pl, lastEdge);
  const [ox, oy, oz] = vec3Scale(tx, ty, tz, finalDist);

  const l = pl.length;
  pl[l - 3] += ox;
  pl[l - 2] += oy;
  pl[l - 1] += oz;

  return pl;
}

export function polyLine3ExtendEndInDirection(pl: PolyLine3, dist: number, x: number, y: number, z: number) {
  const l = pl.length;

  const newPl = polyLine3New(l + 3);
  newPl.set(pl);

  [newPl[l], newPl[l + 1], newPl[l + 2]] = ray3Eval(pl[l - 3], pl[l - 2], pl[l - 1], x, y, z, dist);
  return newPl;
}

export function polyLine3StartPoint(pl: PolyLine3): Vec3 {
  vec3Result[0] = pl[0];
  vec3Result[1] = pl[1];
  vec3Result[2] = pl[2];
  return vec3Result;
}

export function polyLine3EndPoint(pl: PolyLine3): Vec3 {
  vec3Result[0] = pl[pl.length - 3];
  vec3Result[1] = pl[pl.length - 2];
  vec3Result[2] = pl[pl.length - 1];
  return vec3Result;
}

export function polyLine3EdgeStart(pl: PolyLine3, edgeIndex: number): Vec3 {
  const offset = polyLine3RepairEdgeIndex(pl, edgeIndex) * 3;

  vec3Result[0] = pl[offset];
  vec3Result[1] = pl[offset + 1];
  vec3Result[2] = pl[offset + 2];

  return vec3Result;
}

export function polyLine3ExtractEdge(pl: PolyLine3, edgeIndex: number): PolyLine3 {
  const e2 = polyLine3RepairEdgeIndex(pl, edgeIndex) * 3;
  return polyLine3New([pl[e2 + 0], pl[e2 + 1], pl[e2 + 2], pl[e2 + 3], pl[e2 + 4], pl[e2 + 5]]);
}

export function polyLine3EdgeEnd(pl: PolyLine3, edgeIndex: number): Vec3 {
  const offset = polyLine3RepairEdgeIndex(pl, edgeIndex) * 3;

  vec3Result[0] = pl[offset + 3];
  vec3Result[1] = pl[offset + 4];
  vec3Result[2] = pl[offset + 5];

  return vec3Result;
}

export function polyLine3EdgeNormal(pl: PolyLine3, edgeIndex: number, negate: boolean = false) {
  if (polyLine3EdgeCount(pl) < 3) {
    throw new Error("polyLine3EdgeNormal: PolyLine3 must have at least 3 edges");
  }
  const e3 = polyLine3RepairEdgeIndex(pl, edgeIndex) * 3;
  const e6 = polyLine3RepairEdgeIndex(pl, edgeIndex + 1) * 3;
  const currentEdgeX = pl[e3 + 3] - pl[e3];
  const currentEdgeY = pl[e3 + 4] - pl[e3 + 1];
  const currentEdgeZ = pl[e3 + 5] - pl[e3 + 2];
  const nextEdgeX = pl[e6 + 3] - pl[e6];
  const nextEdgeY = pl[e6 + 4] - pl[e6 + 1];
  const nextEdgeZ = pl[e6 + 5] - pl[e6 + 2];
  const [nx, ny, nz] = vec3CrossNormalize(
    currentEdgeX,
    currentEdgeY,
    currentEdgeZ,
    ...vec3Cross(currentEdgeX, currentEdgeY, currentEdgeZ, nextEdgeX, nextEdgeY, nextEdgeZ),
  );
  return negate ? [-nx, -ny, -nz] : [nx, ny, nz];
}

export function polyLine3ExtendStart(pl: PolyLine3, dist: number, limitToEdgeLen: boolean = false) {
  const edgeLen = polyLine3EdgeLength(pl, 0);
  const finalDist = limitToEdgeLen ? Math.max(Math.min(dist, edgeLen), -edgeLen) : dist;

  const [tx, ty, tz] = polyLine3EdgeTangent(pl, 0);
  const [ox, oy, oz] = vec3Scale(tx, ty, tz, finalDist);

  pl[0] -= ox;
  pl[1] -= oy;
  pl[2] -= oz;

  return pl;
}

// polyLine3ExtendEndWithPerpVector extends a PolyLine3 by adding an additional segment that runs perpendiular to
// the guide vector in the plane of the last segment and the guide vector

export function polyLine3ExtendEndWithPerpVector(pl: PolyLine3, x: number, y: number, z: number, dist: number) {
  const [tx, ty, tz] = polyLine3EdgeTangent(pl, polyLine3EdgeCount(pl) - 1);
  const [nx, ny, nz] = vec3Cross(tx, ty, tz, x, y, z);
  const [ox, oy, oz] = vec3CrossNormalize(x, y, z, nx, ny, nz);

  const [lastx, lasty, lastz] = [pl[pl.length - 3], pl[pl.length - 2], pl[pl.length - 1]];

  return polyLine3Concat(pl, ray3Eval(lastx, lasty, lastz, ox, oy, oz, dist));
}

export function polyLine3ExtendStartWithPerpVector(pl: PolyLine3, x: number, y: number, z: number, dist: number) {
  const [tx, ty, tz] = polyLine3EdgeTangent(pl, 0);
  const [nx, ny, nz] = vec3Cross(tx, ty, tz, x, y, z);
  const [ox, oy, oz] = vec3CrossNormalize(x, y, z, nx, ny, nz);

  return polyLine3Concat(ray3Eval(pl[0], pl[1], pl[2], -ox, -oy, -oz, dist), pl);
}

export function polyLine3ExtendBothEndsWithPerpVector(
  pl: PolyLine3,
  x: number,
  y: number,
  z: number,
  distStart: number,
  distEnd: number,
): PolyLine3 {
  return polyLine3ExtendEndWithPerpVector(polyLine3ExtendStartWithPerpVector(pl, x, y, z, distStart), x, y, z, distEnd);
}

// polyLine3ExtendEndWithCrossVector extends a PolyLine3 by adding an additional segment that runs parallel to
// the guide vector in the plane of the last segment and the guide vector

export function polyLine3ExtendEndWithCrossVector(pl: PolyLine3, x: number, y: number, z: number, dist: number) {
  const [tx, ty, tz] = polyLine3EdgeTangent(pl, polyLine3EdgeCount(pl) - 1);
  const [nx, ny, nz] = vec3CrossNormalize(tx, ty, tz, x, y, z);

  const [lastx, lasty, lastz] = [pl[pl.length - 3], pl[pl.length - 2], pl[pl.length - 1]];

  return polyLine3Concat(pl, ray3Eval(lastx, lasty, lastz, nx, ny, nz, dist));
}

export function polyLine3ExtendStartWithCrossVector(pl: PolyLine3, x: number, y: number, z: number, dist: number) {
  const [tx, ty, tz] = polyLine3EdgeTangent(pl, 0);
  const [nx, ny, nz] = vec3CrossNormalize(tx, ty, tz, x, y, z);

  return polyLine3Concat(ray3Eval(pl[0], pl[1], pl[2], -nx, -ny, -nz, dist), pl);
}

export function polyLine3ExtendBothEndsWithCrossVector(
  pl: PolyLine3,
  x: number,
  y: number,
  z: number,
  distStart: number,
  distEnd: number,
): PolyLine3 {
  return polyLine3ExtendEndWithCrossVector(
    polyLine3ExtendStartWithCrossVector(pl, x, y, z, distStart),
    x,
    y,
    z,
    distEnd,
  );
}

export function polyLine3ScaleX(pl: PolyLine3, s: number) {
  for (let i = 0, l = pl.length; i < l; i += 3) {
    pl[i] *= s;
  }
}

export function polyLine3RepairEdgeIndex(pl: PolyLine3, edgeIndex: number) {
  const l = polyLine3EdgeCount(pl);
  edgeIndex = edgeIndex % l;
  if (edgeIndex < 0) {
    edgeIndex = l + edgeIndex;
  }
  return edgeIndex;
}

export function polyLine3RepairVertexIndex(pl: PolyLine3, vertexIndex: number) {
  const l = polyLine3VertexCount(pl);
  vertexIndex = vertexIndex % l;
  if (vertexIndex < 0) {
    vertexIndex = l + vertexIndex;
  }
  return vertexIndex;
}

export function polyLine3Edge(pl: PolyLine3, edgeIndex: number): PolyLine3 {
  const offset = polyLine3RepairEdgeIndex(pl, edgeIndex) * 3;
  const npl = polyLine3New(6);
  npl[0] = pl[offset];
  npl[1] = pl[offset + 1];
  npl[2] = pl[offset + 2];
  npl[3] = pl[offset + 3];
  npl[4] = pl[offset + 4];
  npl[5] = pl[offset + 5];
  return npl;
}

export function polyLine3Vertex(pl: PolyLine3, vertexIndex: number): Vec3 {
  const offset = polyLine3RepairVertexIndex(pl, vertexIndex) * 3;

  vec3Result[0] = pl[offset];
  vec3Result[1] = pl[offset + 1];
  vec3Result[2] = pl[offset + 2];
  return vec3Result;
}

export function polyLine3EdgeCount(pl: PolyLine3): number {
  return pl.length / 3 - 1;
}

export function polyLine3VertexCount(pl: PolyLine3): number {
  return pl.length / 3;
}

export function polyLine3EvalEdge(pl: PolyLine3, edgeIndex: number, t: number, normalized?: boolean): Vec3 {
  const e3 = edgeIndex * 3;
  return line3Eval(pl[e3 + 0], pl[e3 + 1], pl[e3 + 2], pl[e3 + 3], pl[e3 + 4], pl[e3 + 5], t, normalized);
}

export function polyLine3EdgeSlope(pl: PolyLine3, edgeIndex: number): number {
  const e3 = edgeIndex * 3;
  const ax = pl[e3 + 0];
  const ay = pl[e3 + 1];
  const az = pl[e3 + 2];
  const bx = pl[e3 + 3];
  const by = pl[e3 + 4];
  const bz = pl[e3 + 5];

  return line3Slope(ax, ay, az, bx, by, bz);
}

export function polyLine3EdgeLength(pl: PolyLine3, edgeIndex: number): number {
  const e3 = polyLine3RepairEdgeIndex(pl, edgeIndex) * 3;
  return vec3Dist(pl[e3 + 0], pl[e3 + 1], pl[e3 + 2], pl[e3 + 3], pl[e3 + 4], pl[e3 + 5]);
}

export function polyLine3EdgeTangent(pl: PolyLine3, edgeIndex: number, negate: boolean = false): Vec3 {
  const e3 = polyLine3RepairEdgeIndex(pl, edgeIndex) * 3;
  return line3Tangent(pl[e3 + 0], pl[e3 + 1], pl[e3 + 2], pl[e3 + 3], pl[e3 + 4], pl[e3 + 5], negate);
}

export function polyLine3Copy(pl: PolyLine3): PolyLine3 {
  const n = polyLine3New(pl.length);
  n.set(pl);
  return n;
}

export function polyLine3LengthIsLessThan(pl: PolyLine3, maxLength: number): boolean {
  let len = 0.0;
  for (let i = 0, l = pl.length - 3; i < l; i += 3) {
    len += vec3Dist(pl[i], pl[i + 1], pl[i + 2], pl[i + 3], pl[i + 4], pl[i + 5]);
    if (len > maxLength) {
      return false;
    }
  }

  return true;
}

export function polyLine3Length(pl: PolyLine3): number {
  let len = 0.0;
  for (let i = 0, l = pl.length - 3; i < l; i += 3) {
    len += vec3Dist(pl[i], pl[i + 1], pl[i + 2], pl[i + 3], pl[i + 4], pl[i + 5]);
  }

  return len;
}

export function polyLine3AABB3(pl: ArrayLike<number>, tol: number = DIST_EPSILON): AABB3 {
  // init
  const x0 = pl[0];
  const y0 = pl[1];
  const z0 = pl[2];

  let minX = x0 - tol;
  let minY = y0 - tol;
  let minZ = z0 - tol;
  let maxX = x0 + tol;
  let maxY = y0 + tol;
  let maxZ = z0 + tol;

  // expand
  for (let i = 3, l = pl.length; i < l; i += 3) {
    const x = pl[i];
    const y = pl[i + 1];
    const z = pl[i + 2];

    minX = minX < x ? minX : x;
    minY = minY < y ? minY : y;
    minZ = minZ < z ? minZ : z;

    maxX = maxX > x ? maxX : x;
    maxY = maxY > y ? maxY : y;
    maxZ = maxZ > z ? maxZ : z;
  }

  return aabb3New(minX, minY, minZ, maxX, maxY, maxZ);
}

export function polyLine3Simplify(
  pl: PolyLine3,
  distThresholdSq: number = DIST_EPSILON_SQUARED,
  angleThreshold: number = ANGLE_EPSILON,
) {
  pl = polyLine3MergeCoincidentVertices(pl, distThresholdSq);
  return polyLine3MergeCollinearEdges(pl, angleThreshold);
}

export function polyLine3MergeCoincidentVertices(pl: PolyLine3, epsilonSq: number = DIST_EPSILON_SQUARED) {
  const plen = pl.length;
  let cursor = 3;
  for (let i = 0, l = plen - 3; i < l; i += 3) {
    const x0 = pl[i];
    const y0 = pl[i + 1];
    const z0 = pl[i + 2];
    const x1 = pl[i + 3];
    const y1 = pl[i + 4];
    const z1 = pl[i + 5];

    pl[cursor] = x1;
    pl[cursor + 1] = y1;
    pl[cursor + 2] = z1;

    if (vec3DistSquared(x0, y0, z0, x1, y1, z1) > epsilonSq) {
      cursor += 3;
    }
  }

  if (cursor === plen) {
    return pl;
  }

  // reslice
  return pl.slice(0, cursor);
}

export function polyLine3MergeCollinearEdges(pl: PolyLine3, epsilon: number = ANGLE_EPSILON) {
  const closed = polyLine3IsClosed(pl);

  const plen = pl.length;
  let cursor = 3;
  for (let i = 0, l = plen - 3; i < l; i += 3) {
    const x0 = pl[i];
    const y0 = pl[i + 1];
    const z0 = pl[i + 2];

    const x1 = pl[i + 3];
    const y1 = pl[i + 4];
    const z1 = pl[i + 5];

    const x2 = pl[i + 6];
    const y2 = pl[i + 7];
    const z2 = pl[i + 8];

    pl[cursor] = x1;
    pl[cursor + 1] = y1;
    pl[cursor + 2] = z1;

    if (vec3SequenceIsTurning(x0, y0, z0, x1, y1, z1, x2, y2, z2, epsilon)) {
      cursor += 3;
    }
  }

  cursor += 3;

  if (
    closed &&
    pl.length >= 6 &&
    !vec3SequenceIsTurning(
      pl[cursor - 6],
      pl[cursor - 5],
      pl[cursor - 4],
      pl[0],
      pl[1],
      pl[2],
      pl[3],
      pl[4],
      pl[5],
      epsilon,
    )
  ) {
    // the start edge and end edge are collinear

    // this means we need to combine these edges into one, removing the initial vertex
    pl[0] = pl[cursor - 6];
    pl[1] = pl[cursor - 5];
    pl[2] = pl[cursor - 4];

    cursor -= 3;
  }

  if (cursor === plen) {
    return pl;
  }

  // reslice
  return pl.slice(0, cursor);
}

export function polyLine3ReverseCopy(pl: PolyLine3): PolyLine3 {
  return polyLine3Reverse(polyLine3Copy(pl));
}

export function polyLine3ToPolyLine2IgnoreZ(pl3: PolyLine3): PolyLine2 {
  const pointCount = pl3.length / 3;
  const pl2 = polyLine2New(pointCount * 2);
  for (let i = 0; i < pointCount; ++i) {
    pl2[i * 2] = pl3[i * 3];
    pl2[i * 2 + 1] = pl3[i * 3 + 1];
  }
  return pl2;
}

export function polyLine3RotateZ(pl: PolyLine3, radians: number, centerX: number = 0, centerY: number = 0) {
  const s = sin(radians);
  const c = cos(radians);

  for (let i = 0, l = pl.length; i < l; i += 3) {
    const x = pl[i] - centerX;
    const y = pl[i + 1] - centerY;

    pl[i] = x * c - y * s + centerX;
    pl[i + 1] = x * s + y * c + centerY;
  }

  return pl;
}

// break a PolyLine3 at its vertices
export function polyLine3Shatter(pl: PolyLine3): PolyLine3[] {
  const segCount = polyLine3EdgeCount(pl);
  const subLines: PolyLine3[] = [];

  for (let i = 0; i < segCount; i += 1) {
    const subLine = polyLine3Edge(pl, i);
    subLines.push(subLine);
  }

  return subLines;
}
